// src/views/admin/ADParametresEleves.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

// ─── COMPOSANT MODAL GÉNÉRIQUE (même pattern que ADEleves) ─────────────────
function Modal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── CONFIGURATION DES DEUX ONGLETS (mêmes endpoints, structure identique) ─
// On factorise complètement le CRUD dans un seul composant générique, piloté
// par la config ci-dessous, pour ne pas dupliquer 2x la même logique.
const buildSections = (service) => ([
  {
    key: "etats-sanitaires",
    label: "États sanitaires",
    icon: "ti-heartbeat",
    getAll: service.getEtatsSanitaires,
    create: service.createEtatSanitaire,
    update: service.updateEtatSanitaire,
    remove: service.deleteEtatSanitaire,
    placeholder: "Ex: Bien portant, Handicap moteur, Asthmatique...",
  },
  {
    key: "situations-sociales",
    label: "Situations sociales",
    icon: "ti-users-group",
    getAll: service.getSituationsSociales,
    create: service.createSituationSociale,
    update: service.updateSituationSociale,
    remove: service.deleteSituationSociale,
    placeholder: "Ex: Rien à signaler, Orphelin de père, Orphelin de mère...",
  },
]);

// ─── COMPOSANT PRINCIPAL ────────────────────────────────────────────────────
export default function ADParametresEleves({ slug }) {
  const service = useAdminService();
  const sections = buildSections(service);
  const toast = useToastHook();

  const [activeTab, setActiveTab] = useState(sections[0].key);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState({}); // { [sectionKey]: [] }
  const [showInactifs, setShowInactifs] = useState(false);

  const [newLibelle, setNewLibelle] = useState("");
  const [modal, setModal] = useState(null); // "edit" | "delete"
  const [selected, setSelected] = useState(null);
  const [editLibelle, setEditLibelle] = useState("");

  const current = sections.find(s => s.key === activeTab);

  useEffect(() => {
    if (slug) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (slug) loadOne(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactifs, activeTab]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        sections.map(s => s.getAll(slug, { actifs_seulement: showInactifs ? "false" : "true" }))
      );
      const map = {};
      sections.forEach((s, i) => { map[s.key] = results[i] || []; });
      setItems(map);
    } catch {
      toast.error("Impossible de charger les référentiels");
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (key) => {
    const section = sections.find(s => s.key === key);
    if (!section) return;
    try {
      const data = await section.getAll(slug, { actifs_seulement: showInactifs ? "false" : "true" });
      setItems(prev => ({ ...prev, [key]: data || [] }));
    } catch {
      toast.error("Impossible de charger la liste");
    }
  };

  // ─── CRÉATION ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const libelle = newLibelle.trim();
    if (!libelle) {
      toast.error("Le libellé est requis");
      return;
    }
    setSaving(true);
    try {
      await current.create(slug, { libelle });
      toast.success(`"${libelle}" ajouté avec succès`);
      setNewLibelle("");
      await loadOne(activeTab);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.libelle?.[0] || data?.non_field_errors?.[0] || data?.error || "Erreur lors de l'ajout";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDownCreate = (e) => {
    if (e.key === "Enter") handleCreate();
  };

  // ─── ÉDITION ───────────────────────────────────────────────────────────
  const openEdit = (item) => {
    setSelected(item);
    setEditLibelle(item.libelle);
    setModal("edit");
  };

  const handleSaveEdit = async () => {
    const libelle = editLibelle.trim();
    if (!libelle) {
      toast.error("Le libellé est requis");
      return;
    }
    setSaving(true);
    try {
      await current.update(slug, selected.id, { libelle });
      toast.success("Modifié avec succès");
      setModal(null);
      await loadOne(activeTab);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.libelle?.[0] || data?.non_field_errors?.[0] || data?.error || "Erreur lors de la modification";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ─── ACTIVER / DÉSACTIVER (toggle rapide) ─────────────────────────────
  const handleToggleActif = async (item) => {
    try {
      await current.update(slug, item.id, { actif: !item.actif });
      toast.success(item.actif ? "Désactivé" : "Réactivé");
      await loadOne(activeTab);
    } catch {
      toast.error("Impossible de modifier le statut");
    }
  };

  // ─── SUPPRESSION (le backend peut renvoyer 200+message si déjà utilisé
  // par des élèves, auquel cas c'est une désactivation et pas une suppression
  // réelle — on adapte le toast en conséquence) ──────────────────────────
  const openDelete = (item) => {
    setSelected(item);
    setModal("delete");
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await current.remove(slug, selected.id);
      if (response?.message) {
        toast.warning(response.message);
      } else {
        toast.success("Supprimé avec succès");
      }
      setModal(null);
      await loadOne(activeTab);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Impossible de supprimer");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  const list = items[activeTab] || [];

  return (
    <div>
      {/* Onglets */}
      <div className="tab-nav">
        {sections.map(s => (
          <button
            key={s.key}
            className={`tab-btn ${activeTab === s.key ? "active" : ""}`}
            onClick={() => setActiveTab(s.key)}
          >
            <i className={`ti ${s.icon}`} /> {s.label} ({(items[s.key] || []).length})
          </button>
        ))}
      </div>

      <div className="sb2">
        <input
          type="text"
          placeholder={current.placeholder}
          value={newLibelle}
          onChange={e => setNewLibelle(e.target.value)}
          onKeyDown={handleKeyDownCreate}
          style={{ minWidth: 320, flex: 1 }}
        />
        <button className="bprim" onClick={handleCreate} disabled={saving}>
          <i className="ti ti-plus" /> Ajouter
        </button>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--m)", marginLeft: "auto" }}>
          <input
            type="checkbox"
            checked={showInactifs}
            onChange={e => setShowInactifs(e.target.checked)}
          />
          Afficher les valeurs désactivées
        </label>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: "60%" }}>Libellé</th>
              <th style={{ width: "20%" }}>Statut</th>
              <th style={{ width: "20%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length > 0 ? (
              list.map(item => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 500 }}>{item.libelle}</td>
                  <td>
                    <span className={`pill ${item.actif ? "info" : "pu"}`}>
                      {item.actif ? "Actif" : "Désactivé"}
                    </span>
                  </td>
                  <td>
                    <div className="act">
                      <button
                        title={item.actif ? "Désactiver" : "Réactiver"}
                        onClick={() => handleToggleActif(item)}
                        style={{ background: item.actif ? "#6c757d" : "#28a745", color: "white" }}
                      >
                        <i className={`ti ${item.actif ? "ti-eye-off" : "ti-eye"}`} />
                      </button>
                      <button
                        title="Modifier"
                        onClick={() => openEdit(item)}
                        style={{ background: "#ffc107", color: "black" }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                      <button className="del" title="Supprimer" onClick={() => openDelete(item)}>
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
                  Aucune valeur configurée pour {current.label.toLowerCase()}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── MODAL ÉDITION ─── */}
      {modal === "edit" && selected && (
        <Modal title={`Modifier — ${current.label}`} onClose={() => setModal(null)}>
          <div className="fg">
            <label>Libellé <span style={{ color: "var(--rd)" }}>*</span></label>
            <input
              type="text"
              value={editLibelle}
              onChange={e => setEditLibelle(e.target.value)}
              autoFocus
            />
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveEdit} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                : <><i className="ti ti-check" /> Enregistrer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── MODAL SUPPRESSION ─── */}
      {modal === "delete" && selected && (
        <Modal title={`Supprimer — ${current.label}`} onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            Supprimer <strong style={{ color: "var(--t)" }}>{selected.libelle}</strong> ?
            <div style={{ color: "var(--h)", marginTop: 8, fontSize: 13 }}>
              <i className="ti ti-info-circle" /> Si cette valeur est déjà utilisée par des élèves, elle sera
              désactivée plutôt que supprimée, pour ne pas casser leur historique.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Suppression...</>
                : <><i className="ti ti-trash" /> Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .tab-nav {
          display: flex; gap: 4px; flex-wrap: wrap;
          background: var(--pr); border-radius: 10px; padding: 8px 12px;
          margin-bottom: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .tab-btn {
          display: flex; align-items: center; gap: 6px; padding: 10px 18px;
          border: none; border-radius: 6px; font-size: 13px; font-weight: 600;
          cursor: pointer; transition: all 0.3s ease; background: transparent; color: #fff;
        }
        .tab-btn:hover { background: rgba(255,255,255,0.15); }
        .tab-btn.active { background: white; color: var(--pr); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .act button {
          width: 30px; height: 30px; border-radius: 4px; border: none; cursor: pointer;
          color: var(--m); display: flex; align-items: center; justify-content: center;
          font-size: 14px; transition: all .2s; background: var(--s2);
        }
        .act button:hover { opacity: 0.8; }
        .act button.del { background: var(--rd); color: white; }
      `}</style>
    </div>
  );
}